import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextButton, TextLink, TextWithIcon } from '@modusign/design-system';
import { getLabelList } from '@modusign/openapi-client-category';
import { getDocument as getExternalDocument } from '@modusign/openapi-client-external-document';
import { getContractLinkById, getTemplate } from '@modusign/openapi-client-template';
import { useState } from 'react';
import { useQuery } from 'react-query';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import {
  ResourceModal,
  ResourceModalBody,
  ResourceModalHeader,
} from '@app/components/Modal/ResourceModal';
import IconLabel from '@app/features/label/components/IconLabel';
import workspaceService from '@app/libs/workspaceService';
import { getDocumentsByIdDeprecated } from '@app/store/transport-layer/document/documents';

import LabelInfoModalContent from '../LabelInfoModalContent';

type ResourceType = 'DOCUMENT' | 'EXTERNAL_DOCUMENT' | 'CONTRACT_LINK' | 'TEMPLATE';

const labelFetcher: Record<
  ResourceType,
  (
    id: string,
    labelIds?: string[]
  ) => Promise<{
    resourceName: string;
    labels: Array<{ id: string; name: string; searchUrl?: string }>;
  }>
> = {
  EXTERNAL_DOCUMENT: (id) =>
    getExternalDocument(workspaceService.getWorkspaceId(), id).then((d) => ({
      resourceName: d.title,
      labels: d.labels,
    })),
  DOCUMENT: (id) =>
    getDocumentsByIdDeprecated({
      documentId: id,
      workspaceId: workspaceService.getWorkspaceId(),
    }).then((d) => ({
      resourceName: d.name,
      labels: d.labels,
    })),
  CONTRACT_LINK: (id) =>
    getContractLinkById(workspaceService.getWorkspaceId(), id).then(async (contractLink) => {
      const labels = await (contractLink.labelIds.length > 0
        ? getLabelList(workspaceService.getWorkspaceId(), {
            labelIds: contractLink.labelIds,
            offset: 0,
            limit: 100,
          }).then((res) => res.labels)
        : Promise.resolve([]));

      return {
        resourceName: contractLink.title,
        labels,
      };
    }),
  TEMPLATE: (id, labelIds) =>
    getTemplate(workspaceService.getWorkspaceId(), id).then(async (template) => {
      const labels = await getLabelList(workspaceService.getWorkspaceId(), {
        labelIds,
        offset: 0,
        limit: 100,
      }).then((res) => res.labels);
      return {
        resourceName: template.title,
        labels,
      };
    }),
};

type CommonLabelListModalProps = {
  resourceId: string;
  isOpen: boolean;
  onClose: () => void;
};

type LabelListModalProps =
  | (CommonLabelListModalProps & {
      resourceType: Exclude<ResourceType, 'TEMPLATE'>;
      labelIds?: string[];
    })
  | (CommonLabelListModalProps & {
      resourceType: 'TEMPLATE';
      labelIds: string[];
    });

export default function LabelListModal({
  isOpen,
  resourceId,
  resourceType,
  labelIds,
  onClose,
}: LabelListModalProps) {
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);

  const { isLoading, isError, data, refetch } = useQuery({
    queryKey: ['labelList', resourceType, resourceId],
    queryFn:
      resourceType === 'TEMPLATE'
        ? () => labelFetcher[resourceType](resourceId, labelIds)
        : () => labelFetcher[resourceType](resourceId),
    enabled: workspaceService.getWorkspaceId() !== null,
  });

  if (selectedLabelId) {
    return (
      <ResourceModal isOpen>
        <LabelInfoModalContent
          id={selectedLabelId}
          onBack={() => {
            setSelectedLabelId(null);
            refetch();
          }}
          onClose={onClose}
        />
      </ResourceModal>
    );
  }

  if (!data) {
    return (
      <ResourceModal isOpen>
        <ResourceModalHeader resourceName={''} title="라벨" onClose={onClose} />
        <ResourceModalBody height="200px" position="relative">
          {isLoading && <CenterLoadingSpinner />}
          {isError && (
            <div>
              <Text>불러오기 실패</Text>
              <TextButton ml={3} onClick={() => refetch()}>
                다시 시도하기
              </TextButton>
            </div>
          )}
        </ResourceModalBody>
      </ResourceModal>
    );
  }

  return (
    <ResourceModal isOpen={isOpen}>
      <ResourceModalHeader resourceName={data.resourceName} title="라벨" onClose={onClose} />
      <ResourceModalBody>
        <div>
          <Text fontWeight="medium">적용된 라벨</Text>
          <Text
            bg="statusBackgroundNeutralBase"
            color="textNeutralLight"
            fontSize="xs"
            fontWeight="medium"
            ml="3px"
            px={3}
            py="2px"
            sx={{ borderRadius: '12px' }}
          >
            {data.labels.length}
          </Text>
        </div>
        <Box as="ul" maxHeight="356px" mt={4} overflowY="auto">
          {data.labels.map(({ id, name, searchUrl }) => (
            <Flex
              as="li"
              justifyContent="space-between"
              key={id}
              py={2}
              sx={{
                '&:hover': {
                  bg: 'tableBackgroundNeutralDark',
                  a: {
                    opacity: 1,
                  },
                },
              }}
            >
              <Box
                as="button"
                flex="auto"
                type="button"
                width="0px"
                onClick={() => setSelectedLabelId(id)}
              >
                <IconLabel name={name} />
              </Box>
              {searchUrl && (
                <TextLink sx={{ flex: 'none', opacity: 0 }} to={searchUrl}>
                  <TextWithIcon rightIcon={<FontAwesomeIcon icon={faChevronRight} />}>
                    이 라벨로 검색
                  </TextWithIcon>
                </TextLink>
              )}
            </Flex>
          ))}
        </Box>
      </ResourceModalBody>
    </ResourceModal>
  );
}
